<script setup lang="ts">
import InfoCallout from "@/components/common/InfoCallout.vue";
import type { RecipientSelection } from "@/dashboard/inventory_management/send/sendRecipient";
import SendRecipientPicker from "@/dashboard/inventory_management/send/SendRecipientPicker.vue";
import {
  CNMC_SEDE_URL,
  exportDestinationDisplay,
} from "@/dashboard/production_strategy/issuance-reporting/es/cnmcPresentation";
import {
  cnmcRecipientChecks,
  type CnmcRecipientField,
} from "@/dashboard/production_strategy/issuance-reporting/es/cnmcRecipientRules";
import type { CnmcRecipientDraft } from "@/dashboard/production_strategy/issuance-reporting/es/cnmcTypes";
import { computed, useId } from "vue";
import { useI18n } from "vue-i18n";

/**
 * Send's recipient form for a CNMC transfer or export, laid out like SendRecipientForm, with how the
 * holder files it in the Sede.
 *
 * The name search offers external companies only: Soldera has no CNMC account, and the preview refuses
 * an export into one of its foreign accounts. Picking a company fills its EECS account, which only an
 * export files. A field's errors show once it holds a value; Send keeps its button disabled until the
 * recipient is complete.
 */
const props = defineProps<{
  destinations: { region: string; name: string }[];
}>();
const recipient = defineModel<CnmcRecipientDraft>({ required: true });
/** Company name read from an uploaded document; cleared once the user picks or types a recipient. */
const aiCompanyName = defineModel<string | undefined>("aiCompanyName");

const { t } = useI18n();
const id = useId();

const selection = computed<RecipientSelection>({
  get: () => ({
    companyId: null,
    companyName: recipient.value.name,
    eecsAccountNumber: recipient.value.eecs,
  }),
  set: (picked) => {
    recipient.value = {
      ...recipient.value,
      name: picked.companyName,
      eecs: picked.eecsAccountNumber,
    };
    aiCompanyName.value = undefined;
  },
});

const checks = computed(() =>
  cnmcRecipientChecks(recipient.value.route, props.destinations)
);

const errors = computed(() => ({
  name: errorOf("name"),
  r2: errorOf("r2"),
  eecs: errorOf("eecs"),
}));

const destinationDisplay = computed(() =>
  recipient.value.route === "EXPORT"
    ? exportDestinationDisplay(recipient.value.eecs, props.destinations, t)
    : ""
);

/** The message of the first check a filled-in field fails; null for an empty or valid one. */
const errorOf = (field: CnmcRecipientField): string | null => {
  const value = recipient.value[field];
  if (!value.trim()) return null;
  const failed = Object.values(checks.value[field] ?? {}).find(
    (check) => !check.test(value)
  );
  return failed ? t(failed.message, failed.params ?? {}) : null;
};

const setCode = (field: "r2" | "eecs", event: Event) => {
  recipient.value = {
    ...recipient.value,
    [field]: (event.target as HTMLInputElement).value,
  };
};
</script>

<template>
  <div class="d-flex flex-column gap-3">
    <div class="small fw-semibold">
      {{ t(`cnmc.outbound.recipient_modes.${recipient.route}`) }}
    </div>
    <div>
      <label class="form-label small">{{
        t("dashboard.portfolio.recipientCompanyName")
      }}</label>
      <SendRecipientPicker
        v-model="selection"
        :displayValue="aiCompanyName"
        :includeHosted="false"
      />
      <div v-if="errors.name" class="form-text text-danger">
        {{ errors.name }}
      </div>
    </div>
    <div v-if="recipient.route === 'DOMESTIC'">
      <label :for="`${id}-r2`" class="form-label small">{{
        t("cnmc.recipient_r2")
      }}</label>
      <input
        :id="`${id}-r2`"
        type="text"
        class="form-control form-control-sm"
        :class="{ 'is-invalid': errors.r2 }"
        :value="recipient.r2"
        placeholder="R2-…"
        @input="setCode('r2', $event)"
      />
      <div v-if="errors.r2" class="form-text text-danger">
        {{ errors.r2 }}
      </div>
      <div class="form-text text-muted">{{ t("cnmc.r2_help") }}</div>
    </div>
    <div v-else>
      <label :for="`${id}-eecs`" class="form-label small">{{
        t("dashboard.portfolio.recipient_eecs_account_number")
      }}</label>
      <div class="input-group">
        <input
          :id="`${id}-eecs`"
          type="text"
          class="form-control form-control-sm"
          :class="{ 'is-invalid': errors.eecs }"
          :value="recipient.eecs"
          @input="setCode('eecs', $event)"
        />
        <span
          v-if="destinationDisplay"
          class="input-group-text bg-light text-muted small"
        >
          {{ destinationDisplay }}
        </span>
      </div>
      <div v-if="errors.eecs" class="form-text text-danger">
        {{ errors.eecs }}
      </div>
    </div>
    <InfoCallout icon="file-lines">
      <p class="mb-1">{{ t("cnmc.outbound.explainer.flow") }}</p>
      <p class="mb-1">
        {{ t("cnmc.outbound.explainer.oldest_first") }}
      </p>
      <p v-if="recipient.route === 'EXPORT'" class="mb-1">
        {{ t("cnmc.outbound.explainer.export") }}
      </p>
      <a :href="CNMC_SEDE_URL" target="_blank" rel="noopener noreferrer">{{
        t("cnmc.outbound.sede_link")
      }}</a>
    </InfoCallout>
  </div>
</template>
